import React, { useEffect, useState } from 'react'
import { discountCodeFormatter, DiscountCodeFormSchema, discountCodeSchema, DiscountType } from 'discounts/_shared'
import { FormWrapper } from 'uiComponents/form/form'
import { addYears } from 'date-fns'
import TextInputFieldFormik from 'uiComponents/input/textInput/textInputFieldFormik'
import './discountCodeForm.scss'
import { ComboboxFieldFormik, MultiSelectComboboxFieldFormik } from 'uiComponents/combobox/comboboxFieldFormik'
import NumberInputFieldFormik from 'uiComponents/input/numberInput/numberInputFieldFormik'
import { useGetAllProductsForSelect, useIsLoadingCategoriesOrArticles } from 'products/redux'
import { useGetAllDiscountPoolsForSelect, useIsLoadingDiscountPools } from '../redux'
import { DateRangeFieldFormik } from 'uiComponents/input/date'
import { Checkbox, CheckboxFieldFormik, SingleSelectFieldFormik, Toggle } from 'uiComponents/input'
import { CancelButton } from 'uiComponents/buttons/cancelButton'
import ActionButton from 'uiComponents/buttons'
import { BaseRouteParams } from 'hocs'
import { useParams } from 'react-router-dom'
import { useAddCodeByFormMutation, useEditDiscountCodeMutation } from 'discounts/reduxQuery'
import { ErrorResponse } from '../../../types'
import { DiscountCodeDetails } from 'discounts/types'
import { FormikHelpers, useFormikContext } from 'formik'
import { FormItemName } from 'uiComponents/form/formElements'
import classNames from 'classnames'
import { useCurrency } from 'uiComponents/money'
import { TooltipWrapper } from 'uiComponents/tooltip.new'
import { showErrorMessage } from 'uiComponents/message'

const defaultValues = {
    rate: 'percentage',
    bookingWindow: {
        from: new Date().toISOString(),
        to: addYears(new Date(), 1).toISOString(),
    },
    eventWindow: {
        from: new Date().toISOString(),
        to: addYears(new Date(), 1).toISOString(),
    },
} as Partial<DiscountCodeFormSchema>

interface DiscountCodeFormProps {
    discountType: DiscountType
    afterSubmit?: (discount: DiscountCodeFormSchema) => void
    onCancel?: () => void
}

interface NewDiscountCodeFormProps extends DiscountCodeFormProps {
    selectedPoolUuid?: string
    discount?: never
}

interface EditDiscountCodeFormProps extends DiscountCodeFormProps {
    selectedPoolUuid?: never
    discount: Partial<DiscountCodeFormSchema>
}

const DiscountCodeFormFields = ({
    discountType,
    selectedPoolUuid,
    isNewCode,
    onCancel,
    isLoading,
}: {
    discountType: DiscountType
    selectedPoolUuid?: string
    isNewCode: boolean
    onCancel?: () => void
    isLoading: boolean
}) => {
    const { values, setFieldValue } = useFormikContext<DiscountCodeFormSchema>()
    const products = useGetAllProductsForSelect()
    const loadingProducts = useIsLoadingCategoriesOrArticles()
    const discountPools = useGetAllDiscountPoolsForSelect()
    const loadingPools = useIsLoadingDiscountPools()
    const currencySymbol = useCurrency().symbol
    const [remainderUsableDisabledReason, setRemainderUsableDisabledReason] = useState<string | undefined>(undefined)

    const handleEnableEventWindowToggle = () => {
        const enabled = !values.enableEventWindow
        setFieldValue('enableEventWindow', enabled)
        if (enabled && !values.eventWindow) {
            setFieldValue('eventWindow', defaultValues.eventWindow)
        }
    }

    useEffect(() => {
        if (values.rate === 'percentage') {
            setRemainderUsableDisabledReason('Can only be enabled if "Discount value" is set to "Fixed amount"')
            setFieldValue('remainderUsable', false)
        } else if (!values.enableMaxUses || values.maxUses !== 1) {
            setRemainderUsableDisabledReason('Can only be enabled if "Limit total uses" is set to 1')
            setFieldValue('remainderUsable', false)
        } else {
            setRemainderUsableDisabledReason(undefined)
        }
    }, [values.rate, values.enableMaxUses, values.maxUses])

    return (
        <>
            <div className="row">
                <TextInputFieldFormik
                    name="code"
                    className="column"
                    icon="Barcode"
                    placeholder="SALE25"
                    label="Code"
                    whiteBackground
                    border
                />
                <ComboboxFieldFormik
                    id="poolUuid"
                    label="Pool"
                    icon="ScanBarcode"
                    className="column"
                    options={discountPools}
                    loading={loadingPools}
                    value={selectedPoolUuid}
                    disabled={!isNewCode}
                />
            </div>
            {discountType === 'product' && (
                <div className="row">
                    <div className="column product">
                        <MultiSelectComboboxFieldFormik
                            options={products}
                            id="products.uuids"
                            label="Products"
                            loading={loadingProducts}
                        />
                    </div>
                    <div className={classNames('column product-amount', { enabled: values.enableProductAmount })}>
                        <FormItemName
                            className="column-title togglable"
                            onClick={() => {
                                setFieldValue('enableProductAmount', !values.enableProductAmount)
                            }}
                        >
                            <Toggle
                                id="product-amount-toggle"
                                title="Limit uses per product"
                                className="toggle"
                                isOn={values.enableProductAmount}
                                onClick={() => {
                                    setFieldValue('enableProductAmount', !values.enableProductAmount)
                                }}
                            />
                            <div>Limit uses per product</div>
                        </FormItemName>
                        <NumberInputFieldFormik
                            name="products.amount"
                            disabled={!values.enableProductAmount}
                            placeholder=""
                            whiteBackground
                            border
                        />
                        <div className="explanation">Within each order.</div>
                    </div>
                </div>
            )}
            <div className="row">
                <div className="column booking-window">
                    <DateRangeFieldFormik
                        name="bookingWindow"
                        label="Booking window"
                        lastAvailableDay={addYears(new Date(), 5)}
                        allowComparison={false}
                        openOnCurrentAndNextMonth
                    />
                    <div className="explanation">When this discount can be applied at checkout</div>
                </div>
                <div className={classNames('column event-window', { enabled: values.enableEventWindow })}>
                    <FormItemName className="column-title togglable" onClick={handleEnableEventWindowToggle}>
                        <Toggle
                            id="event-window-toggle"
                            title="Limit event window"
                            className="toggle"
                            isOn={values.enableEventWindow}
                            onClick={handleEnableEventWindowToggle}
                        />
                        <div>Limit event window</div>
                    </FormItemName>
                    {values.enableEventWindow ? (
                        <DateRangeFieldFormik
                            name="eventWindow"
                            lastAvailableDay={addYears(new Date(), 5)}
                            allowComparison={false}
                            openOnCurrentAndNextMonth
                        />
                    ) : (
                        <div className="event-window-placeholder" />
                    )}
                    <div className="explanation">Eligible visit dates using this discount</div>
                </div>
            </div>
            <div className="row">
                <div className="column discount-value">
                    <SingleSelectFieldFormik
                        name="rate"
                        className="rate"
                        label="Discount value"
                        options={[
                            { value: 'percentage', name: 'Percentage' },
                            { value: 'flat', name: 'Fixed amount' },
                        ]}
                        validateOnChange={false}
                        validateOnBlur={false}
                    />
                    <NumberInputFieldFormik
                        name="reductionRate"
                        placeholder=""
                        whiteBackground
                        border
                        maxLength={6}
                        prefix={values.rate === 'flat' ? currencySymbol : ''}
                        suffix={values.rate === 'percentage' ? '%' : ''}
                    />
                </div>
                <div className={classNames('column uses', { enabled: values.enableMaxUses })}>
                    <FormItemName
                        className="column-title togglable"
                        onClick={() => {
                            setFieldValue('enableMaxUses', !values.enableMaxUses)
                        }}
                    >
                        <Toggle
                            id="max-uses-toggle"
                            title="Limit total uses"
                            className="toggle"
                            isOn={values.enableMaxUses}
                            onClick={() => {
                                setFieldValue('enableMaxUses', !values.enableMaxUses)
                            }}
                        />
                        <div>Limit total uses</div>
                    </FormItemName>
                    <NumberInputFieldFormik
                        name="maxUses"
                        disabled={!values.enableMaxUses}
                        placeholder=""
                        whiteBackground
                        border
                    />
                </div>
            </div>
            <div className="row boxed">
                <CheckboxFieldFormik variant="inline" name="remainderUsable">
                    {(props) => (
                        <TooltipWrapper
                            className={classNames('remainder-usable', { disabled: !!remainderUsableDisabledReason })}
                            text={remainderUsableDisabledReason}
                            position="bottom"
                            arrowPosition="left"
                        >
                            <Checkbox {...props} disabled={!!remainderUsableDisabledReason} />
                            <div className="text">
                                <div className="title">Make the remainder usable</div>
                                <div className="explanation">Allow any leftover amount to be used on new orders.</div>
                            </div>
                        </TooltipWrapper>
                    )}
                </CheckboxFieldFormik>
            </div>
            <div className="row actions">
                {onCancel && (
                    <CancelButton className="back-button" secondary size="large" onClick={onCancel}>
                        {isNewCode ? 'Back' : 'Cancel'}
                    </CancelButton>
                )}
                <ActionButton size="large" type="submit" kind="default" disabled={isLoading}>
                    {isNewCode ? 'Create code' : 'Save code'}
                </ActionButton>
            </div>
        </>
    )
}

export const DiscountCodeForm = ({
    discountType,
    selectedPoolUuid,
    discount,
    onCancel,
    afterSubmit,
}: NewDiscountCodeFormProps | EditDiscountCodeFormProps) => {
    const isNewCode = !discount
    const routeParams = useParams<BaseRouteParams>()
    const [submitForm, { isLoading }] = isNewCode ? useAddCodeByFormMutation() : useEditDiscountCodeMutation()

    const onSubmit = async (
        values: DiscountCodeFormSchema,
        { setFieldError }: FormikHelpers<DiscountCodeFormSchema>,
    ) => {
        const formattedFormData = discountCodeFormatter.toDomain(values)
        const dataToSubmit = {
            accountSlug: routeParams.accountSlug,
            formData: formattedFormData,
            poolUuid: values.poolUuid!,
        } as any

        if (!isNewCode) {
            dataToSubmit.codeUuid = values.id!
        }

        submitForm(dataToSubmit)
            .unwrap()
            .then((data: DiscountCodeDetails) => {
                return afterSubmit?.(values)
            })
            .catch((data: ErrorResponse<DiscountCodeDetails>) => {
                if (data.error.code === 'coupon_already_exists') {
                    setFieldError('code', data.error.message)
                } else if (data.error.message) {
                    showErrorMessage(data.error.message)
                } else if (data.error.errors) {
                    showErrorMessage(JSON.stringify(data.error.errors))
                }
            })
    }

    return (
        <FormWrapper
            className="discount-code-form modal-body"
            validationSchema={discountCodeSchema}
            initialValues={discount ?? { ...defaultValues, type: discountType, poolUuid: selectedPoolUuid }}
            onSubmit={onSubmit}
            validateOnChange={false}
            validateOnBlur={false}
        >
            <DiscountCodeFormFields
                discountType={discountType}
                selectedPoolUuid={selectedPoolUuid}
                isNewCode={isNewCode}
                onCancel={onCancel}
                isLoading={isLoading}
            />
        </FormWrapper>
    )
}
